import { useEffect, useMemo, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { POLLING_INTERVALS } from '../../../constants/phi'
import type { PHIPrediction, PHIContainer } from '../../../types/phi'
import { StudyModel } from '@common/config/api/client'

// Define types for timepoint submission data
type ContainerSubmission = {
  container_submission_id: string
  dicom_json: Record<string, unknown> | null
  [key: string]: unknown
}

type TimePointSubmissionData = {
  data?: {
    timepoint_submission: {
      container_submissions: ContainerSubmission[]
      [key: string]: unknown
    }
    [key: string]: unknown
  }
  [key: string]: unknown
}

interface UsePHIProcessingProps {
  phiData: { containers?: PHIContainer[] } | undefined
  timePointSubmissionData: TimePointSubmissionData | undefined
  timepointSubmissionId: string
  onSetPHIPredictions: (predictions: Record<string, PHIPrediction[]>) => void
  onSetPhiWorkflowState: (inProgress: boolean, conversionInProgress: boolean, status: string) => void
}

export function usePHIProcessing({
  phiData,
  timePointSubmissionData,
  timepointSubmissionId,
  onSetPHIPredictions,
  onSetPhiWorkflowState,
}: UsePHIProcessingProps) {
  const queryClient = useQueryClient()

  // Memoize the processed predictions to prevent constant re-processing
  const processedPredictions = useMemo(() => {
    if (!phiData?.containers) return {}

    console.log('Processing PHI data')

    const containers = phiData.containers
    if (!containers || !Array.isArray(containers)) return {}

    const predictionsMap: Record<string, PHIPrediction[]> = {}

    containers.forEach((container) => {
      container.exams.forEach((exam) => {
        exam.series.forEach((series) => {
          if (series.predictions && series.predictions.length > 0) {
            series.predictions.forEach((prediction) => {
              const key = prediction.sop_instance_uid
              if (!predictionsMap[key]) {
                predictionsMap[key] = []
              }

              // Set default values for action, scope, and source
              const enhancedPrediction: PHIPrediction = {
                ...prediction,
                id: prediction.id || undefined,
                action: (prediction.action as 'apply' | 'remove') || 'apply',
                scope: (prediction.scope as 'single' | 'all') || 'single',
                source: (prediction.source as 'AI' | 'USER') || 'AI',
                exam_metadata_id: exam.exam_metadata_id,
                series_metadata_id: series.series_metadata_id,
                study_instance_uid: exam.study_instance_uid,
                series_instance_uid: series.series_instance_uid,
              }

              predictionsMap[key].push(enhancedPrediction)
            })
          }
        })
      })
    })
    console.log('Predictions map:', predictionsMap)
    return predictionsMap
  }, [phiData?.containers])

  // Process PHI predictions data
  useEffect(() => {
    onSetPHIPredictions(processedPredictions)
  }, [processedPredictions, onSetPHIPredictions])

  // Enhanced effect to track PHI workflow status and trigger timepoint data refresh
  useEffect(() => {
    if (!phiData?.containers) {
      onSetPhiWorkflowState(false, false, 'idle')
      return
    }

    // Determine the most specific workflow status
    let workflowStatus = 'idle'
    let anyInProgress = false
    let nonDicomConversionInProgress = false

    // Check container statuses
    for (const container of phiData.containers) {
      if (container.overall_status === 'in_progress') {
        anyInProgress = true
        workflowStatus = 'processing'
      } else if (container.overall_status === 'queued') {
        anyInProgress = true
        workflowStatus = 'queued'
      }
    }

    // Check specifically for conversion in progress (no dicom_json yet)
    if (anyInProgress && timePointSubmissionData?.data?.timepoint_submission.container_submissions) {
      nonDicomConversionInProgress = timePointSubmissionData.data.timepoint_submission.container_submissions.some(
        (submission: ContainerSubmission) =>
          submission.dicom_json === null || Object.keys(submission.dicom_json || {}).length === 0,
      )

      if (nonDicomConversionInProgress) {
        workflowStatus = 'converting'
      }
    }

    onSetPhiWorkflowState(anyInProgress, nonDicomConversionInProgress || false, workflowStatus)

    // If we're in progress, periodically invalidate the timepoint submission query
    // to refresh the dicom_json data as it becomes available
    let intervalId: number | undefined

    if (anyInProgress) {
      intervalId = window.setInterval(() => {
        queryClient.invalidateQueries({
          queryKey: ['TimePointSubmissionDetails', timepointSubmissionId],
        })
      }, POLLING_INTERVALS.phiStatus)
    }

    return () => {
      if (intervalId) window.clearInterval(intervalId)
    }
  }, [phiData?.containers, timePointSubmissionData, timepointSubmissionId, queryClient, onSetPhiWorkflowState])

  // Helper functions - use useCallback to prevent unnecessary re-renders
  const getSeriesWithPhiDetected = useCallback(
    (
      phiData: { containers?: PHIContainer[] } | undefined,
      studyInstanceUID: string,
      dataToRender: Record<string, StudyModel>,
    ): string[] => {
      const containers = phiData?.containers
      if (!containers || !Array.isArray(containers)) return []

      const seriesInStudy = dataToRender[studyInstanceUID]?.series || []
      const seriesWithPhi: string[] = []

      seriesInStudy.forEach((series) => {
        const seriesUID = series.SeriesInstanceUID

        const hasPhi = containers.some((container) =>
          container.exams.some((exam) =>
            exam.series.some((s) => s.series_instance_uid === seriesUID && s.predictions && s.predictions.length > 0),
          ),
        )

        if (hasPhi) {
          seriesWithPhi.push(seriesUID)
        }
      })

      return seriesWithPhi
    },
    [],
  )

  const getSeriesPHIStatus = useCallback(
    (seriesInstanceUID: string): string => {
      if (!phiData?.containers) return 'unknown'

      const containers = phiData.containers
      if (!containers || !Array.isArray(containers)) return 'unknown'

      for (const container of containers) {
        for (const exam of container.exams) {
          const series = exam.series.find((s) => s.series_instance_uid === seriesInstanceUID)
          if (series) {
            return series.phi_status
          }
        }
      }

      return 'phi_not_detected'
    },
    [phiData?.containers],
  )

  const getStatusMessage = useCallback((workflowStatus: string) => {
    switch (workflowStatus) {
      case 'converting':
        return 'Converting non-DICOM files to DICOM format. This may take several minutes...'
      case 'processing':
        return 'PHI redaction in progress. AI is analyzing your images for personal health information...'
      case 'queued':
        return 'PHI redaction job queued. Processing will begin shortly...'
      default:
        return 'Checking PHI redaction status...'
    }
  }, [])

  return {
    getSeriesWithPhiDetected,
    getSeriesPHIStatus,
    getStatusMessage,
  }
}
